import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * The `sections` value a hand-rolled cart add should post, so the server re-renders
 * the drawer's line items IN THE SAME ROUND TRIP as the header bubble.
 *
 * CUSTOM (Stuburt, 2026-08-23). Six add paths in this theme POST to /cart/add.js by
 * hand — the three PDP buy boxes, the card quick-pick, the lander quick-shop sheet
 * and the pack stage — and every one of them asked only for 'cart-icon-bubble'. The
 * drawer therefore had no fresh markup to morph, so cart-items-component fell back
 * to a whole second network render of the header section, and #openWhenFilled sat
 * waiting on it. On a cold cart (0 -> 1 item) that wait regularly outran the 3s
 * deadline and the sheet silently never opened at all; on a warm cart it opened
 * showing the PREVIOUS contents and then jumped. Asking for the drawer's own section
 * here makes the morph synchronous — exactly as it already is on the one path that
 * uses Horizon's own product form (assets/product-form.js, which collects the same
 * ids). Two sections is well inside the Section Rendering API's limit of five.
 *
 * Published on `window` rather than only exported because three of the six callers
 * are inline ES5 section scripts, which cannot import. Every caller guards for its
 * absence and falls back to the bubble alone, so a click landing before this module
 * evaluates degrades to the old behaviour instead of throwing.
 *
 * @returns {string} Comma-joined section ids for the `sections` form field.
 */
export function cartAddSections() {
  const ids = new Set(['cart-icon-bubble']);

  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id) ids.add(id);
  }

  return Array.from(ids).join(',');
}

// @ts-ignore - deliberate global for the inline ES5 callers described above.
window.stuburtCartAddSections = cartAddSections;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #openObserver = null;

  /** @type {number} */
  #openTimeout = 0;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /** @type {MutationObserver | null} */
  #emptyWatcher = null;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#watchForEmpty);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopWatchingForEmpty);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#watchForEmpty);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopWatchingForEmpty);
    this.#stopWatchingForEmpty();
    this.#historyAbortController?.abort();
    this.#cancelPendingOpen();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // CUSTOM (Stuburt, 2026-08-19): take scroll restoration off the browser for the life
    // of this entry. The entry is pushed while the body is locked (position:fixed), so the
    // scroll offset recorded against it is 0 — and the history.back() on close then
    // "restores" the page to the top, after closeDialog has already put it back where it
    // belonged. closeDialog owns the scroll position here; the browser must not.
    if (!history.state?.cartDrawerOpen) {
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    if (this.#previousScrollRestoration) {
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // CUSTOM (Stuburt, 2026-08-19): CartAddEvent shares the 'cart:update' name with
    // quantity changes AND with the theme's own automated adds, so stock's bare
    // auto-open check would slide the sheet open over the customer when they never
    // asked. Filter to a genuine customer add — the same test the branded mini-cart
    // applied before the sheet took over as the add-to-cart feedback:
    //   source 'product-form-component' = a real Add press (PDP form or card quick-pick)
    //   quiet                           = an add the customer didn't press
    //                                     (stuburt-free-shipping-boost, stuburt-gift-keychain)
    //   didError                        = nothing went in, so there is nothing to show
    //
    // CUSTOM (Stuburt, 2026-08-23): `deferDrawer` is a genuine customer add that
    // still must not open the sheet HERE. It is set by the wardrobe lander's adds,
    // which walk the page to the next chapter first — opening now would lock the
    // body (scroll-lock) part-way through that smooth scroll and strand it. Those
    // callers hold the flag, let the morph land off this same event, and then call
    // openWhenReady() once the page has settled.
    const data = event.detail?.data ?? {};
    const isCustomerAdd = data.source === 'product-form-component' && !data.quiet && !data.didError;

    if (this.hasAttribute('auto-open') && isCustomerAdd && !data.deferDrawer) {
      this.#openWhenFilled();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Collapses the sheet when the cart goes empty underneath it.
   *
   * CUSTOM (Stuburt, 2026-08-19). Remove the last line and the sheet used to sit there
   * announcing an empty cart — the customer has to close the thing to get back to the
   * shop. Sliding it away instead says the same thing and returns the page.
   *
   * Watches the DOM rather than the cart:update event because the removal is optimistic:
   * cart-items swaps in the empty template as soon as the last row goes, before the
   * server response that carries the new count. Watching the markup collapses the sheet
   * on that swap instead of a network round trip later.
   *
   * A sheet that was ALREADY empty when it opened is left alone — that is someone tapping
   * the cart icon on an empty cart, and it must stay open to say so. Only a cart that goes
   * empty while you are looking at it collapses.
   */
  #watchForEmpty = () => {
    this.#stopWatchingForEmpty();
    if (this.querySelector('#cart-drawer-heading-empty')) return;

    this.#emptyWatcher = new MutationObserver(() => {
      if (!this.refs.dialog?.open) return;
      if (!this.querySelector('#cart-drawer-heading-empty')) return;
      this.#stopWatchingForEmpty();
      this.closeDialog();
    });
    this.#emptyWatcher.observe(this, { childList: true, subtree: true });
  };

  #stopWatchingForEmpty = () => {
    this.#emptyWatcher?.disconnect();
    this.#emptyWatcher = null;
  };

  #cancelPendingOpen() {
    this.#openObserver?.disconnect();
    this.#openObserver = null;
    window.clearTimeout(this.#openTimeout);
    this.#openTimeout = 0;
  }

  /**
   * Opens the sheet only once the re-rendered cart lines are actually in the DOM.
   *
   * CUSTOM (Stuburt, 2026-08-19). The sheet and the cart lines are rendered from the
   * SAME `cart:update` event, and this component's listener runs first — it upgrades
   * before the nested cart-items-component that does the morph. Opening straight away
   * therefore slides in the PRE-add markup: on a 0->1 add that is the empty state, which
   * is a small auto-height sheet (`.cart-drawer--empty`), so the panel appeared saying
   * the cart was empty and then snapped to full size mid-slide.
   *
   * The readiness signal is the ABSENCE of the empty-state heading. Do not reach for
   * `.cart-drawer__content` — it looks like the filled-only wrapper but header-actions
   * renders it in BOTH branches, so it is true even on an empty cart and the wait never
   * happens. `#cart-drawer-heading-empty` is rendered only by the empty branch.
   *
   * Then open on a SETTLED frame. The readiness signal fires from a MutationObserver
   * microtask, which can land in the middle of the morph — the empty heading is gone
   * but the rest of the sheet is still being rewritten. Opening there starts the slide
   * while the browser is still doing the morph's style and layout work, so the first
   * frames drop and the animation reads as fast and snatchy instead of the smooth glide
   * you get from a cart-icon click (where nothing else is happening). Two frames of
   * daylight: the first lets the morph task finish and its layout commit, the second
   * starts the animation clean. showDialog() adds its own rAF before showModal().
   *
   * It does NOT fail open. An earlier version showed the sheet regardless once a short
   * timer expired, which is how the sheet twice slid in announcing "your cart is empty"
   * on a cart that wasn't: when the add response carries no section HTML, cart-items
   * falls back to fetching the section, and that can outlast any timer worth having. A
   * sheet that contradicts the cart is worse than no sheet — the header count already
   * confirms the add — so if the re-render hasn't landed by the deadline, stand down
   * silently and let the next open render properly.
   */
  #openWhenFilled() {
    if (this.#openObserver || this.#openTimeout) return;

    const isFilled = () => !this.querySelector('#cart-drawer-heading-empty');
    const open = () => {
      this.#cancelPendingOpen();
      requestAnimationFrame(() => requestAnimationFrame(() => this.showDialog()));
    };

    // Already filled (any add after the first): the morph runs synchronously later in
    // this same dispatch, and the two frames above land after it.
    if (isFilled()) {
      open();
      return;
    }

    this.#openObserver = new MutationObserver(() => {
      if (isFilled()) open();
    });
    this.#openObserver.observe(this, { childList: true, subtree: true });

    // Generous, because the fallback path is a network render of the whole section — but
    // it only ever cancels. Reaching it with the sheet still empty means the render lost,
    // and the one thing we must not do is open on markup that says the cart is empty.
    this.#openTimeout = window.setTimeout(() => {
      if (isFilled()) open();
      else this.#cancelPendingOpen();
    }, 3000);
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Opens the sheet once the re-rendered lines have actually landed — the same
   * wait #handleCartAdd performs, exposed as a public control.
   *
   * CUSTOM (Stuburt, 2026-08-23). For the `deferDrawer` callers (see #handleCartAdd):
   * the wardrobe lander's adds scroll the page first and open the cart after, so
   * they need the readiness wait at a moment of their choosing rather than on the
   * event. Use this and not open(): open() shows the dialog immediately, which on a
   * cold cart is the empty state. Honours `auto-open` so that turning the attribute
   * off still turns every automatic open off, in one place.
   *
   * `refresh` covers the back-navigation case: a PDP add that started from the
   * wardrobe lander walks the customer BACK to the lander, and mobile Safari serves
   * that from the bfcache — same DOM, scripts not re-run — so this drawer still
   * holds the markup from BEFORE the add. On a cart that was empty then, the wait
   * below would sit on an empty-state heading nothing is going to clear and time
   * out silently. Re-render first, await the morph, and open on real lines.
   *
   * @param {boolean} [refresh] - Re-render this drawer's lines from the server first.
   */
  async openWhenReady(refresh = false) {
    if (!this.hasAttribute('auto-open')) return;

    if (refresh) {
      const sectionId = /** @type {HTMLElement | null} */ (
        this.querySelector('cart-items-component[data-section-id]')
      )?.dataset.sectionId;

      // On failure fall through and open on whatever is already there — a stale
      // sheet is recoverable, a sheet that never opens is not.
      if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false }).catch(() => {});
    }

    this.#openWhenFilled();
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
